import { AppResourcePage } from '../../pages/app-resource-base';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { createConcurrencyCheck, ResourceLocker } from '../../common/resource-lock';
import { customElement } from 'lit/decorators.js';
import {
  FabricationJobContainer,
  FabricationJobContainerManager
} from '../../../fabrication-jobs/data/fabrication-job-container';
import {
  FabricationJobDataEntryView,
  FabricationJobView
} from '../../../fabrication-jobs/views/fabrication-job-detail-view';
import { fireQuickInformationToast } from '../../../../webmodule-common/other/toast-away';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { goStaticURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { LangAppFabricationJobPage } from '../../../../localization/scripts/dealer-franchisee/fabrication-jobs/pages/app-fabrication-job-lang';
import { ModalViewBase } from '../../../../webmodule-common/other/ui/data-entry-screen-base';
import { NullPromise } from '../../../../webmodule-common/interop/types/misc';
import { SSEFabricationJob } from '../../../api/fabrication-job-api';
import { WMEventSource } from '../../../api/event-source';

export class FabricationJobLock extends ResourceLocker {
  constructor(id: string, lockRevokedEvent: () => Promise<void>, concurrencyCheckEvent?: () => Promise<boolean>) {
    super(id, 'fabricationjob', 'fabrication-job', lockRevokedEvent, concurrencyCheckEvent);
  }
}

@customElement('app-fabrication-job')
export class AppFabricationJobPage extends AppResourcePage {
  private containerManager?: FabricationJobContainerManager;

  async createModalEditor(id: string): NullPromise<ModalViewBase> {
    const container = new FabricationJobContainer(id, null, []);
    this.containerManager = new FabricationJobContainerManager(container);
    this.containerManager.forceLocked = () => !this.lock?.isLockOwner;
    await this.containerManager.needsFabricationJob();

    return await constructAsync(
      new FabricationJobView({
        containerManager: this.containerManager,
        onDelete: async () => {
          const success = await this.containerManager!.deleteJob();
          if (success) {
            goStaticURL('/fabrication-jobs');
          }
        }
      })
    );
  }

  async redirectPath(): NullPromise<string> {
    return '/fabrication-jobs';
  }

  meta() {
    return {
      title: () => {
        const job = this.containerManager?.fabricationJob;
        return job
          ? `Job #${job.jobNumber} ${job.title || LangAppFabricationJobPage.fabricationJobTitle()}`
          : LangAppFabricationJobPage.fabricationJobTitle();
      },
      description: 'Fabrication job detail'
    };
  }

  protected createLock(id: string): ResourceLocker | undefined {
    const lockRevokedEvent = async () => {
      await this.view?.abandonAndClose(true);
      goStaticURL('/fabrication-jobs');
    };

    const concurrencyCheck = createConcurrencyCheck('FABRICATION.FABRICATIONJOB', id, async () => {
      await this.containerManager?.needsFabricationJob();
      return this.containerManager?.fabricationJob?.recordVersion ?? '';
    });

    return new FabricationJobLock(id, lockRevokedEvent, concurrencyCheck);
  }

  // SSE: Listen for changes to this fabrication job
  private _sseHandler = async (_data: unknown) => {
    const data = _data as SSEFabricationJob | undefined;
    if (!data) return;
    if (data.id !== this.resourceId) return;
    // Skip changes made by the current user — they already see their own updates
    const currentUserId = getCurrentUser()?.id;
    if (currentUserId && data.lastModifiedUserId === currentUserId) return;

    await this.containerManager?.loadFromServer();
    const detailView = this.view as FabricationJobDataEntryView | null;
    if (detailView?.refreshItemsTable) {
      await detailView.refreshItemsTable();
    }
    this.requestUpdate();
    fireQuickInformationToast(LangAppFabricationJobPage.updatedByAnotherUser());
  };

  connectedCallback() {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.fabricationJob, this._sseHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.fabricationJob, this._sseHandler);
  }
}
